import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableBody, TableContainer } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ConsumersTableFilter } from '../components';
import { ConsumersTableRow, TableHeader, TablePagination } from '../components/table';
import { consumersTableHead } from '../app/config';
import useConsumers from '../hooks/useConsumers';
import { apiClient } from '../providers/apiClient';

import './screens.styles.scss';
import '../components/table/table.styles.scss';

const DEFAULTS = { pageSize: 6, pageNumber: 1, orderBy: null, orderDirection: null };

const toUrlObj = (f) => {
  const o = {};
  if (f.pageSize) o.pageSize = String(f.pageSize);
  if (f.pageNumber) o.pageNumber = String(f.pageNumber);
  if (f.orderBy) o.orderBy = f.orderBy;
  if (f.orderDirection) o.orderDirection = f.orderDirection;
  if (f.search) o.search = f.search;
  return o;
};

const fromUrl = (sp) => {
  const num = (v, d) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : d;
  };
  const dir = (v) => (v === 'asc' || v === 'desc' ? v : null);
  return {
    pageSize: num(sp.get('pageSize'), DEFAULTS.pageSize),
    pageNumber: num(sp.get('pageNumber'), DEFAULTS.pageNumber),
    orderBy: sp.get('orderBy') || DEFAULTS.orderBy,
    orderDirection: dir(sp.get('orderDirection')) || DEFAULTS.orderDirection,
  };
};

const sameParams = (a, b) => {
  const A = Object.entries(a).filter(([, v]) => v !== null && v !== undefined && v !== '');
  const B = Object.entries(b).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (A.length !== B.length) return false;
  return A.every(([k, v]) => String(v) === String(b[k]));
};

const ConsumersScreen = () => {
  const [open, setOpen] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();


  const initial = useMemo(() => fromUrl(searchParams), []);

  const {
    data: consumers,
    error,
    loading,
    filter,
    setFilter,
    nextPage,
    previousPage,
    refresh,
    pagination,
  } = useConsumers({...initial, isConsumer: true});

  useEffect(() => {
    const incoming = fromUrl(searchParams);
    const current  = {
      pageSize: filter.pageSize,
      pageNumber: filter.pageNumber,
      orderBy: filter.orderBy,
      orderDirection: filter.orderDirection,
      search: filter.search,
    };

    if (!location.search) {
      setOpen(null);
      return;
    }

    if (!sameParams(incoming, toUrlObj(current))) {
      Object.entries(incoming).forEach(([k, v]) => setFilter(k, v));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.search]);
  
  

  useEffect(() => {
    const desired = toUrlObj(filter);
    const current = Object.fromEntries(searchParams.entries());
    if (!sameParams(desired, current)) {
      setSearchParams(desired, { replace: true });
    }
  }, [
    filter.pageSize,
    filter.pageNumber,
    filter.orderBy,
    filter.orderDirection,
    filter.search,
    searchParams,
    setSearchParams
  ]);

  const showOnPage = useCallback(
      (value) => {
        setFilter('pageSize', value);
        setFilter('pageNumber', 1);
      },
      [setFilter]
  );

  const handleRequestSort = useCallback(
      (event, property) => {
        const direction =
            filter.orderBy === property && filter.orderDirection === 'asc' ? 'desc' : 'asc';
        setFilter('orderBy', property);
        setFilter('orderDirection', direction);
      },
      [filter.orderBy, filter.orderDirection, setFilter]
  );

  const handleRowClick = useCallback((id) => {
    setOpen((curr) => (curr === id ? null : id));
  }, []);

  const handleStatusChange = useCallback(
      async (consumer) => {
        const { id, isBlocked, fullName } = consumer;
        const title = isBlocked ? 'Unblock Account' : 'Block Account';
        const message = isBlocked
            ? `Are you sure you want to unblock the consumer account ${fullName}?`
            : `Are you sure you want to block the consumer account ${fullName}?`;

        if (!window.confirm(`${title}\n\n${message}`)) return;

        try {
          await apiClient.put(`/api/Account/changeStatus?id=${consumer.id}`);
          refresh();
        } catch (e) {
          console.error(e);
        }
      },
      []
  );

  const handleDelete = useCallback(
      async (consumer) => {
        if (!window.confirm(`Are you sure that you want to delete ${consumer.fullName}?`)) return;
        try {
          await apiClient.delete(`/api/Account?id=${consumer.id}`);
          refresh();
        } catch (e) {
          console.error(e);
        }
      },
      []
  );

  const rowCount = useMemo(() => consumers.length, [consumers]);
  const beforeUrl = `${location.pathname}${location.search}`;

  return (
      <div className="fk-screen">
        {loading && <div style={{ padding: 8 }}>Ładowanie…</div>}
        {error && <div style={{ padding: 8, color: 'crimson' }}>Błąd: {error}</div>}

        <ConsumersTableFilter filter={filter} setFilter={setFilter} onSubmit={refresh} />

        <TableContainer>
          <Table size="medium" className="fk-table">
            <TableHeader
                crud
                items={consumersTableHead}
                orderBy={filter.orderBy}
                rowCount={rowCount}
                order={filter.orderDirection}
                onRequestSort={handleRequestSort}
                screen="ConsumersScreen"
            />

            <TableBody>
              {consumers.map((consumer) => (
                  <ConsumersTableRow
                      key={consumer.id}
                      consumer={consumer}
                      open={open === consumer.id}
                      onRowClick={handleRowClick}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                      before={beforeUrl}
                  />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
            filter={pagination} // { hasNext, hasPrevious, pageSize, pageNumber }
            handlePreviousPage={previousPage}
            handleNextPage={nextPage}
            showOnPage={showOnPage}
        />
      </div>
  );
};

export default ConsumersScreen;
